/**
 * @fileoverview Geometry and layout for the glaze color wheel.
 *
 * Positions are normalized to a unit square (0–1) containing the wheel, so it
 * renders at any size:
 * - **Angle = hue**, 0° (red) at the top, increasing clockwise — the same
 *   orientation as CSS `conic-gradient`.
 * - **Radius = lightness** across the hue ring: lightest at the inner edge,
 *   darkest at the outer edge.
 * - **Neutrals** (low saturation, so no meaningful hue) sit in a gray core
 *   disc at the center, placed top (light) to bottom (dark).
 *
 * Many glazes share or nearly share a color (Gloss/Matte pairs often carry the
 * same hex), so dots are nudged apart until none overlap — otherwise some
 * glazes would be unreachable even under the magnifier.
 *
 * @module colorWheel
 */
import { hexToHsl } from '../../../shared/utils/hexToHsl.js';

export { hexToHsl };

/** Saturation below which a glaze is treated as neutral. */
export const NEUTRAL_SATURATION = 0.12;
/** Radii as a fraction of the wheel radius. */
export const CORE_RADIUS = 0.22;
export const RING_INNER = 0.3;
export const RING_OUTER = 0.98;

/** Reference wheel diameter (px) the overlap relaxation is computed at. */
const REF_SIZE = 400;
/** Minimum distance between dot centers at the reference size (px). */
const MIN_DIST = 12;
/** Keep dot centers this far (px, at reference size) inside their region. */
const EDGE_PAD = 6;
const RELAX_ITERATIONS = 80;

/**
 * Ideal (pre-relaxation) position of a color on the wheel.
 * @param {string} hex
 * @returns {{x: number, y: number, neutral: boolean}|null} Normalized 0–1
 */
export function wheelPosition(hex) {
    const hsl = hexToHsl(hex);
    if (!hsl) return null;
    if (hsl.s < NEUTRAL_SATURATION) {
        const span = CORE_RADIUS * 0.5 * 0.8;
        return { x: 0.5, y: 0.5 + (0.5 - hsl.l) * 2 * span, neutral: true };
    }
    const r = 0.5 * (RING_INNER + (1 - hsl.l) * (RING_OUTER - RING_INNER));
    const theta = (hsl.h * Math.PI) / 180;
    return { x: 0.5 + r * Math.sin(theta), y: 0.5 - r * Math.cos(theta), neutral: false };
}

/** Pulls a point back into its region (core disc or hue ring) radially. */
function clampToRegion(p) {
    const pad = EDGE_PAD / REF_SIZE;
    const [minR, maxR] = p.neutral
        ? [0, CORE_RADIUS * 0.5 - pad]
        : [RING_INNER * 0.5 + pad, RING_OUTER * 0.5 - pad];
    let dx = p.x - 0.5;
    let dy = p.y - 0.5;
    let r = Math.hypot(dx, dy);
    if (r < 1e-9) {
        if (minR === 0) return;
        dx = 0;
        dy = -1;
        r = 1;
    }
    const clamped = Math.min(maxR, Math.max(minR, r));
    p.x = 0.5 + (dx / r) * clamped;
    p.y = 0.5 + (dy / r) * clamped;
}

/** Keyboard/reading order: neutrals top→bottom, then the ring clockwise from red. */
function orderKey(d) {
    if (d.neutral) return [0, d.y, d.x];
    const angle = (Math.atan2(d.x - 0.5, -(d.y - 0.5)) + 2 * Math.PI) % (2 * Math.PI);
    return [1, angle, Math.hypot(d.x - 0.5, d.y - 0.5)];
}

function compareOrder(a, b) {
    const ka = orderKey(a);
    const kb = orderKey(b);
    return ka[0] - kb[0] || ka[1] - kb[1] || ka[2] - kb[2];
}

/**
 * Lays out glazes on the wheel, pushing overlapping dots apart.
 * Deterministic: the same palette always produces the same layout.
 * @param {Object<string, string>} colors - Glaze name → hex
 * @returns {Array<{name: string, hex: string, x: number, y: number, neutral: boolean}>}
 *   In keyboard order (neutrals, then clockwise around the ring).
 */
export function layoutWheel(colors) {
    const dots = [];
    for (const [name, hex] of Object.entries(colors || {})) {
        const pos = wheelPosition(hex);
        if (pos) dots.push({ name, hex, ...pos });
    }
    // Stable starting order so ties resolve the same way every time
    dots.sort((a, b) => compareOrder(a, b) || a.name.localeCompare(b.name));

    for (let iter = 0; iter < RELAX_ITERATIONS; iter++) {
        let moved = false;
        for (let i = 0; i < dots.length; i++) {
            for (let j = i + 1; j < dots.length; j++) {
                const a = dots[i];
                const b = dots[j];
                if (a.neutral !== b.neutral) continue;
                let dx = (b.x - a.x) * REF_SIZE;
                let dy = (b.y - a.y) * REF_SIZE;
                let dist = Math.hypot(dx, dy);
                if (dist >= MIN_DIST) continue;
                if (dist < 1e-6) {
                    // Identical colors: separate along a fixed, index-based angle
                    const angle = (j * 2.399963) % (2 * Math.PI);
                    dx = Math.cos(angle);
                    dy = Math.sin(angle);
                    dist = 1;
                }
                const push = (MIN_DIST - dist) / 2 / REF_SIZE;
                a.x -= (dx / dist) * push;
                a.y -= (dy / dist) * push;
                b.x += (dx / dist) * push;
                b.y += (dy / dist) * push;
                clampToRegion(a);
                clampToRegion(b);
                moved = true;
            }
        }
        if (!moved) break;
    }
    return dots.sort(compareOrder);
}

/**
 * The dot nearest a point, within `maxDist`.
 * @param {Array<{x: number, y: number}>} dots - Normalized positions
 * @param {number} px - Point x in field pixels
 * @param {number} py - Point y in field pixels
 * @param {number} size - Field (wheel) size in px
 * @param {number} maxDist - Max distance in px
 * @returns {Object|null}
 */
export function nearestDot(dots, px, py, size, maxDist) {
    let best = null;
    let bestDist = maxDist;
    for (const d of dots) {
        const dist = Math.hypot(d.x * size - px, d.y * size - py);
        if (dist <= bestDist) {
            best = d;
            bestDist = dist;
        }
    }
    return best;
}

const pct = (v) => `${(v * 100).toFixed(2)}%`;

/** CSS background for the hue ring: lightness overlay on a conic hue sweep. */
export const RING_BACKGROUND = [
    `radial-gradient(circle closest-side, #fff ${pct(RING_INNER)}, rgb(255 255 255 / 0) ${pct(
        (RING_INNER + RING_OUTER) / 2,
    )}, rgb(0 0 0 / 0) ${pct((RING_INNER + RING_OUTER) / 2)}, #000 ${pct(RING_OUTER)})`,
    'conic-gradient(from 0deg, hsl(0 70% 50%), hsl(60 70% 50%), hsl(120 70% 50%), hsl(180 70% 50%), hsl(240 70% 50%), hsl(300 70% 50%), hsl(360 70% 50%))',
].join(', ');

/** CSS background for the neutral core: light at top, dark at bottom. */
export const CORE_BACKGROUND = 'linear-gradient(to bottom, #fff, #000)';
